/**
 * The document on screen in the page's own viewer (components/DocumentViewer),
 * one at a time. Shown inside the page rather than in a new tab because an
 * embedded browser drops a blob tab with no error; a frame in the page shows
 * the same PDF, with the viewer's own print and download, everywhere.
 *
 * A file (PDF or image) carries `url`; a printout the page built itself
 * carries its `html`, shown with scripts off; a print page of this app carries
 * its `page` path. The last two print from the viewer's Print button.
 */

export type ShownDocument =
  | { title: string; url: string; html?: undefined; page?: undefined }
  | { title: string; html: string; url?: undefined; page?: undefined }
  | { title: string; page: string; url?: undefined; html?: undefined };

type Listener = (doc: ShownDocument | null) => void;

let shown: ShownDocument | null = null;
const listeners = new Set<Listener>();

function emit(): void {
  for (const l of listeners) l(shown);
}

function replace(next: ShownDocument | null): void {
  if (shown?.url) URL.revokeObjectURL(shown.url);
  shown = next;
  emit();
}

/** Callers go through showDocument in api.ts, which decides what is safe to show. */
export function showInPage(blob: Blob, title: string): void {
  replace({ title, url: URL.createObjectURL(blob) });
}

/**
 * A printout the page built as its own HTML document. It opens to read and
 * prints only from the viewer's Print button, never by itself.
 */
export function showPrintout(html: string, title: string): void {
  replace({ title, html });
}

/**
 * A print page of this app (a path such as /hr/fire-register/print). Print
 * stays unavailable until the page marks itself `data-print-ready="true"`, so
 * nothing prints before everything it shows has loaded.
 */
export function showAppPage(path: string, title: string): void {
  replace({ title, page: path });
}

export function closeShownDocument(): void {
  if (!shown) return;
  replace(null);
}

export function subscribeToShownDocument(listener: Listener): () => void {
  listeners.add(listener);
  listener(shown);
  return () => {
    listeners.delete(listener);
  };
}
